import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchInput } from "../components/SearchInput";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadRailwayStations } from "../utils/dataLoader";
import type { RailwayStation } from "../types/data";

export function RailwayCodesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  const { data: stations, loading, error } = useDataLoader(loadRailwayStations);

  // Debounce search term to avoid lagging while typing
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  const filteredStations = useMemo(() => {
    const source = stations ?? [];
    if (!debouncedSearch) return source;

    const term = debouncedSearch.toLowerCase();
    return source.filter(
      (station) =>
        station.code.toLowerCase().includes(term) ||
        station.name.toLowerCase().includes(term) ||
        station.city.toLowerCase().includes(term) ||
        station.state.toLowerCase().includes(term) ||
        station.zone.toLowerCase().includes(term)
    );
  }, [debouncedSearch, stations]);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian Railway Station Codes Database",
    "description": "Complete database of Indian Railway station codes with station name, city, state, and railway zone information",
    "keywords": ["railway codes", "station codes", "IRCTC station codes", "Indian railway codes"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/railway-codes`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      <SEO
        {...routeSeo('railway-codes')}
        keywords="railway codes, station codes, IRCTC station codes, Indian railway station finder, railway zone search"
        structuredData={structuredData}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Railway Station Codes</h1>
        <p className="text-sm sm:text-base text-gray-600">Search for railway station codes by station name, city, or zone</p>
      </div>

      {loading && (
        <div className="text-center py-8 text-gray-500">Loading stations...</div>
      )}
      {error && (
        <div className="text-center py-8 text-red-600">Failed to load railway stations. Please try again later.</div>
      )}

      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search station codes, names, or cities..."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredStations.map((station) => (
          <Card key={station.code} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="text-2xl font-bold text-red-600">{station.code}</span>
                <Badge variant="secondary">{station.state}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Station Name</p>
                  <p className="font-medium">{station.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">City</p>
                  <p className="font-medium">{station.city}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Railway Zone</p>
                  <p className="text-sm text-gray-600">{station.zone}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStations.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No railway stations found matching your search.</p>
        </div>
      )}
    </div>
    </>
  );
}